// =====================================================================
// ACRYLMALL 商品ページの価格計算パイプライン
//
// 旧 updateDiscountAmount.js と calculateGrandTotalPriceDisplay.js を
// 1 つの update pipeline に統合したもの。
//
// パイプラインの流れ:
//   [trigger]
//     → 1. updateGrandTotal()         GrandTotalPriceDisplay を更新
//     → 2. calculateTotalOptionPrice() TotalOptionPriceDisplay を更新
//                                      (内部で更に updateGrandTotal を再実行)
//     → 3. updateDiscountAmount()     Quote アプリ用 _qq_discount_amount を更新
//
// trigger:
//   - DOMContentLoaded (初回)
//   - TotalPriceDisplay の childList/characterData 変化 (Quote アプリが書き換え)
//   - TotalOptionPriceDisplay の childList/characterData 変化
//   - quantity-selector__input の input/change
//   - quantity-selector__button のクリック (50ms 後に再実行)
//   - product-form の change (オプション radio/checkbox/select 変更)
//   - variant:changed / product:rerender / product:rerendered /
//     product:variant-changed (Focal 13 の variant 切替・再描画イベント)
//
// 重要な順序: Quote アプリ用 hidden input の更新は **必ず最後**。
// Quote アプリは送信ボタン押下時に hidden input の最新値を読むため、
// totalPrice が更新された後に discount を再計算する必要がある。
//
// observer/listener の重複登録防止:
//   - quantity-selector__input は registeredQuantityInput で同一性比較
//   - quantity-selector__button は dataset.acrylmallGrandTotalBound マーカー
//   - product-form は registeredForm で同一性比較
//   - 各 MutationObserver は setup 冒頭で disconnect() してから再作成
// =====================================================================

(function () {
  let observerForPrice = null;
  let observerForOption = null;
  let registeredQuantityInput = null;
  let registeredForm = null;

  function extractNumber(text) {
    return parseInt((text || '').replace(/[^0-9]/g, ''), 10);
  }

  function formatPrice(price) {
    return price.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  }

  // ===== 共通: 商品 form を起点に DOM をスコープする =====
  // mini-cart も .quantity-selector__input を持っているので document 全体から
  // 拾うと mini-cart の数量を誤って取ってしまう恐れがある。
  //
  // Focal 13 では quantity-selector が <form> の外に配置されて
  // form="<id>" 属性で紐付けされる構造なので、form 内 DOM ではなく
  // form 属性で関連付けられた input/button を選択する。
  function findProductForm() {
    return (
      document.querySelector('form.shopify-product-form[id^="product-form-main"]') ||
      document.querySelector('form.shopify-product-form[id^="product-form-template"]') ||
      document.querySelector('form.shopify-product-form')
    );
  }

  function getProductFormId() {
    // 注意: form 内に <input name="id"> があると form.id プロパティが
    // 入力要素に上書きされて返ってくるため、必ず getAttribute('id') で
    // 属性値を直接読む。
    const form = findProductForm();
    return form ? form.getAttribute('id') : null;
  }

  function getProductQuantityInput() {
    const formId = getProductFormId();
    if (!formId) {
      const form = findProductForm();
      return form ? form.querySelector('.quantity-selector__input') : null;
    }
    // 1. form 属性で紐付けされた input (Focal 13 quantity-selector の通常パターン)
    const byFormAttr = document.querySelector(`.quantity-selector__input[form="${CSS.escape(formId)}"]`);
    if (byFormAttr) return byFormAttr;
    // 2. fallback: form 内 input
    const form = findProductForm();
    return form ? form.querySelector('.quantity-selector__input') : null;
  }

  function getProductQuantityButtons() {
    // input の親 quantity-selector 内のボタンを取る (mini-cart 側を除外)
    const input = getProductQuantityInput();
    if (!input) return [];
    const qs = input.closest('quantity-selector');
    return qs ? qs.querySelectorAll('.quantity-selector__button') : [];
  }

  // ===== 共通: GPO config を読んで「one_time_charge な dropdown の label」を集める =====
  // GPO アプリは dropdown の advanced_addon 設定を window.GPOConfigs.options[*].elements
  // にしか持っておらず、DOM dataset には出力しない。そのため select の合計計算には
  // この config から label を引き当てる必要がある。
  function collectOneTimeChargeLabels() {
    const labels = new Set();
    const opts = window.GPOConfigs && window.GPOConfigs.options;
    if (!opts) return labels;
    function walk(elements) {
      if (!Array.isArray(elements)) return;
      elements.forEach((el) => {
        if (el && el.type === 'group' && Array.isArray(el.elements)) {
          walk(el.elements);
        }
        if (el && el.type === 'dropdown' && el.advanced_addon === 'one_time_charge' && el.label) {
          labels.add(el.label);
        }
      });
    }
    Object.values(opts).forEach((cfg) => {
      if (cfg && Array.isArray(cfg.elements)) walk(cfg.elements);
    });
    return labels;
  }

  // ===== Block 1: GrandTotal (商品+オプション合計表示) =====
  function updateGrandTotal() {
    const totalPriceDisplay       = document.getElementById('TotalPriceDisplay');
    const totalOptionPriceDisplay = document.getElementById('TotalOptionPriceDisplay');
    const grandTotalPriceDisplay  = document.getElementById('GrandTotalPriceDisplay');
    if (!totalPriceDisplay || !totalOptionPriceDisplay || !grandTotalPriceDisplay) return;

    const rawTotalPrice = extractNumber(totalPriceDisplay.textContent);
    const basePrice = (function () {
      if (!isNaN(rawTotalPrice)) return rawTotalPrice;
      // TotalPriceDisplay が空のフォールバック: UnitPriceValue × quantity
      const unitPriceElement = document.getElementById('UnitPriceValue');
      const rawUnitPrice = unitPriceElement ? extractNumber(unitPriceElement.textContent) : NaN;
      const unitPrice = isNaN(rawUnitPrice) ? 0 : rawUnitPrice;
      const quantityInput = getProductQuantityInput();
      const quantityRaw = quantityInput ? quantityInput.value : '';
      const quantity = parseInt(quantityRaw, 10) || 1;
      return unitPrice * quantity;
    })();

    const rawOptionPrice = extractNumber(totalOptionPriceDisplay.textContent);
    const optionPrice = isNaN(rawOptionPrice) ? 0 : rawOptionPrice;
    const grandTotal = basePrice + optionPrice;
    grandTotalPriceDisplay.textContent = `${grandTotal.toLocaleString()}円（税込）`;
  }

  // ===== Block 2: Option Price (オプション radio/checkbox/select の追加金額) =====
  function calculateTotalOptionPrice() {
    const form = findProductForm();
    const totalOptionPriceDisplay = document.getElementById('TotalOptionPriceDisplay');
    const totalPriceDisplay = document.getElementById('TotalPriceDisplay');
    if (!form || !totalOptionPriceDisplay || !totalPriceDisplay) return;
    const quantityInput = getProductQuantityInput();

    const totalPriceDisplayValue = parseInt(totalPriceDisplay.textContent.replace(/,/g, '').replace('円', ''), 10);
    if (totalPriceDisplayValue === 0) {
      totalOptionPriceDisplay.textContent = '0円';
      return;
    }

    const quantity = parseInt(quantityInput?.value, 10) || 1;
    let totalPrice = 0;

    const optionInputs = form.querySelectorAll('input[type="radio"], input[type="checkbox"]');
    optionInputs.forEach((input) => {
      if (!input.checked) return;
      const label = input.nextElementSibling;
      if (!label) return;
      const addOnSpan = label.querySelector('.addOn');
      if (!addOnSpan) return;
      const addOnText = addOnSpan.textContent.match(/\+@([\d,]+)円/);
      if (!addOnText) return;
      const price = parseInt(addOnText[1].replace(/,/g, ''), 10);
      // GPO アプリは <input> に data-advanced-addon="one_time_charge" を付ける
      // (HTML 属性は hyphen、JS dataset では advancedAddon の camelCase)。
      // この値が 'one_time_charge' の場合は「カートにつき 1 個」(数量に乗算しない)。
      if (input.dataset.advancedAddon === 'one_time_charge') {
        totalPrice += price;
      } else {
        totalPrice += price * quantity;
      }
    });

    // GPO アプリの dropdown は select 要素 / option 要素に
    // data-advanced-addon を出力しないので、window.GPOConfigs から
    // label → advanced_addon のマップを作って one_time_charge を判定する。
    // 例: 「■分納指定」dropdown は one_time_charge (カートにつき 1 個課金)
    //     「■金具」dropdown は default (qty に乗算)
    const oneTimeChargeLabels = collectOneTimeChargeLabels();

    const selectInputs = form.querySelectorAll('select');
    selectInputs.forEach((select) => {
      const selectedOption = select.options[select.selectedIndex];
      if (!selectedOption) return;
      const addOnSpan = selectedOption.textContent.match(/\+@([\d,]+)円/);
      if (!addOnSpan) return;
      const price = parseInt(addOnSpan[1].replace(/,/g, ''), 10);
      // select の name 属性から label を取り出す: properties[■分納指定] → ■分納指定
      const labelMatch = (select.name || '').match(/^properties\[(.+)\]$/);
      const label = labelMatch ? labelMatch[1] : null;
      if (label && oneTimeChargeLabels.has(label)) {
        totalPrice += price;
      } else {
        totalPrice += price * quantity;
      }
    });

    totalOptionPriceDisplay.textContent = `${formatPrice(totalPrice)}円`;
  }

  // ===== Block 3: Quote アプリ用 hidden input (_qq_discount_amount) =====
  function updateDiscountAmount() {
    const totalPriceDisplay = document.getElementById('TotalPriceDisplay');
    const discountInput = document.getElementById('_qq_discount_amount');
    const quantityInput = getProductQuantityInput();
    if (!totalPriceDisplay || !discountInput) return;

    const totalPrice = parseInt(totalPriceDisplay.textContent.replace(/[^0-9]/g, ''), 10);
    if (isNaN(totalPrice)) return;

    const quantity = quantityInput ? (parseInt(quantityInput.value, 10) || 1) : 1;
    const unitPrice = totalPrice / quantity;
    const discountAmount = Math.max((10000 - unitPrice) * 100, 0);

    discountInput.value = discountAmount;
  }

  // ===== Pipeline =====
  // recalcFullPipeline: option 変化由来 (form change / quantity / button) と
  //   TotalPriceDisplay 変化由来 (Quote アプリ書き込み等) はどちらも同じ手順
  //   (option 再計算 → grand total → discount) で良い。
  // recalcGrandOnly:    TotalOptionPriceDisplay 変化由来 (option 再計算の結果) は
  //   grand total と discount だけ更新。calculateTotalOptionPrice をここで呼ぶと
  //   書き戻しで無限ループになるので呼ばない。
  function recalcFullPipeline() {
    calculateTotalOptionPrice();
    updateGrandTotal();
    updateDiscountAmount();
  }
  function recalcGrandOnly() {
    updateGrandTotal();
    updateDiscountAmount();
  }

  // ===== Setup: Observer + listener の登録 =====
  function setupObservers() {
    // form 起点でスコープを取る (mini-cart の数量 input を誤って拾わないため)
    const form = findProductForm();

    // (a) TotalPriceDisplay 変化 → 全パイプライン再実行
    if (observerForPrice) {
      observerForPrice.disconnect();
      observerForPrice = null;
    }
    const totalPriceElement = document.getElementById('TotalPriceDisplay');
    if (!totalPriceElement) return;
    observerForPrice = new MutationObserver(recalcFullPipeline);
    observerForPrice.observe(totalPriceElement, { childList: true, subtree: true, characterData: true });

    // (a2) TotalOptionPriceDisplay 変化 → grand total + discount のみ更新
    //     (calculateTotalOptionPrice をここで呼ぶと書き戻しで無限ループする)
    if (observerForOption) {
      observerForOption.disconnect();
      observerForOption = null;
    }
    const totalOptionPriceElement = document.getElementById('TotalOptionPriceDisplay');
    if (totalOptionPriceElement) {
      observerForOption = new MutationObserver(recalcGrandOnly);
      observerForOption.observe(totalOptionPriceElement, { childList: true, subtree: true, characterData: true });
    }

    // (b) quantity input の入力/変更 → 全パイプライン再実行 (重複登録防止)
    //     form 属性で紐付けられた input にスコープして mini-cart の input と衝突回避
    const quantityInput = getProductQuantityInput();
    if (quantityInput && quantityInput !== registeredQuantityInput) {
      quantityInput.addEventListener('input', recalcFullPipeline);
      quantityInput.addEventListener('change', recalcFullPipeline);
      registeredQuantityInput = quantityInput;
    }

    // (c) +/- ボタン (form 属性経由でスコープ + 重複登録防止: dataset マーカー)
    getProductQuantityButtons().forEach((btn) => {
      if (btn.dataset.acrylmallGrandTotalBound === 'true') return;
      btn.dataset.acrylmallGrandTotalBound = 'true';
      btn.addEventListener('click', () => setTimeout(recalcFullPipeline, 50));
    });

    // (d) product-form の change (オプション radio/checkbox/select 変更)
    //     form は冒頭で取得済み
    if (form && form !== registeredForm) {
      form.addEventListener('change', recalcFullPipeline);
      registeredForm = form;
    }

    // (e) 初回呼び出し
    setTimeout(recalcFullPipeline, 100);
  }

  // ===== Entry point =====
  function setupAll() {
    setupObservers();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', setupAll);
  } else {
    setupAll();
  }

  // Focal 13 の variant 切替・再描画イベント (form 上で dispatch & bubbles する想定)
  // setupAll は disconnect → 再 observe するので重複しない
  document.addEventListener('variant:changed', () => setTimeout(setupAll, 50));
  document.addEventListener('product:rerender', () => setTimeout(setupAll, 50));
  document.addEventListener('product:rerendered', () => setTimeout(setupAll, 50));
  document.addEventListener('product:variant-changed', () => setTimeout(setupAll, 50));
})();
